import type { CSSProperties, HTMLAttributes } from 'react'

import { cn } from '#utils'
import { DottedOverlay } from './DottedOverlay'
import {
  gradientVariants,
  gradientDirectionCss,
  type GradientVariant,
  type GradientDirection,
} from './gradientVariants'

type DotsPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'

type GradientOverlayProps = {
  /** Custom gradient colors. If not provided, use variant instead */
  gradient?: { color1: string; color2: string }
  /** Preset gradient variant */
  variant?: GradientVariant
  /** Direction of the gradient */
  to?: GradientDirection
  /** Whether to show the dotted overlay pattern */
  showDots?: boolean
  dotsPosition?: DotsPosition
  /** Aria-hidden attribute for the dots */
  dotsAriaHidden?: boolean
  /** Custom className for the dots */
  dotsClassName?: string
  dotsViewBox?: string
} & HTMLAttributes<HTMLDivElement>

/**
 * A pure visual component that renders a gradient background with an optional dotted overlay.
 * This atom provides no layout or content opinions - consumers are responsible for positioning
 * their own content on top of this gradient.
 */
export function GradientOverlay({
  gradient,
  variant,
  to = 'bottom-right',
  showDots = true,
  dotsPosition = 'top-right',
  dotsAriaHidden = true,
  dotsClassName,
  dotsViewBox,
  className,
  style,
  ...restProps
}: GradientOverlayProps) {
  const resolvedGradient = gradient ?? (variant ? gradientVariants[variant] : null)

  const computedStyle: CSSProperties | undefined = resolvedGradient
    ? {
        backgroundColor: resolvedGradient.color1,
        backgroundImage: `linear-gradient(${gradientDirectionCss[to]}, ${resolvedGradient.color1}, ${resolvedGradient.color2})`,
      }
    : undefined

  const dotsStyle: CSSProperties = (() => {
    switch (dotsPosition) {
      case 'top-left':
        return {
          top: 0,
          left: 0,
          right: 'auto',
          bottom: 'auto',
          transformOrigin: 'top left',
        }
      case 'bottom-right':
        return {
          top: 'auto',
          left: 'auto',
          right: 0,
          bottom: 0,
          transformOrigin: 'bottom right',
        }
      case 'bottom-left':
        return {
          top: 'auto',
          left: 0,
          right: 'auto',
          bottom: 0,
          transformOrigin: 'bottom left',
        }
      case 'top-right':
      default:
        return {
          top: 0,
          left: 'auto',
          right: 0,
          bottom: 'auto',
          transformOrigin: 'top right',
        }
    }
  })()

  const dotsPreserveAspectRatio = (() => {
    switch (dotsPosition) {
      case 'top-left':
        return 'xMinYMin meet'
      case 'top-right':
        return 'xMaxYMin meet'
      case 'bottom-left':
        return 'xMinYMax meet'
      case 'bottom-right':
      default:
        return 'xMaxYMax meet'
    }
  })()

  const effectiveDotsViewBox =
    dotsViewBox ??
    (() => {
      switch (dotsPosition) {
        case 'bottom-left':
        case 'bottom-right':
          return '0 -304 207 83'
        case 'top-left':
        case 'top-right':
        default:
          return '0 0 207 133'
      }
    })()

  return (
    <div
      {...restProps}
      className={cn('absolute inset-0 overflow-hidden', className)}
      style={{
        ...computedStyle,
        ...style,
      }}
      data-component="GradientOverlay"
    >
      {showDots ? (
        <DottedOverlay
          ariaHidden={dotsAriaHidden}
          className={cn(
            'pointer-events-none absolute h-[70%] w-[70%] min-h-20 min-w-20 scale-150 text-white',
            dotsClassName,
          )}
          style={dotsStyle}
          preserveAspectRatio={dotsPreserveAspectRatio}
          viewBox={effectiveDotsViewBox}
        />
      ) : null}
    </div>
  )
}
